import { getAuth } from 'firebase/auth';
import { collection, query, where, getDocs, getDoc, doc, documentId } from 'firebase/firestore';
import { db } from '../firebase';
import { geohashQueryBounds, distanceBetween } from 'geofire-common';
import { normalizeNearbyGem, sortNearbyGems } from '../utils/nearbyDiscovery';
import { getSponsoredBusinessMarkerMap } from './sponsoredAvatarService';
import { applyBusinessGemLabels } from '../utils/businessLocationLabel';
import { isCapsuleEnded } from '../utils/capsuleAvailability';

let nearByTimeCapulesRadius = 15000
let unCoverTimeCapulesRadius = 15000

// Load /config.json once and reuse it (it's near-static). The previous
// "?nocache=<timestamp>" buster defeated HTTP caching and refetched on every
// invalid-radius fallback. On failure we reset so a later call can retry.
let appConfigPromise = null;
function loadAppConfig() {
    if (!appConfigPromise) {
        appConfigPromise = fetch("/config.json")
            .then((res) => res.json())
            .catch((err) => {
                console.warn("Failed to load /config.json:", err);
                appConfigPromise = null;
                return {};
            });
    }
    return appConfigPromise;
}
const warnedInvalidLocationCapsuleIds = new Set();

function isValidGeoLocation(lat, lng) {
    const latitude = Number(lat);
    const longitude = Number(lng);
    return (
        Number.isFinite(latitude) &&
        Number.isFinite(longitude) &&
        latitude >= -90 &&
        latitude <= 90 &&
        longitude >= -180 &&
        longitude <= 180
    );
}

function warnInvalidLocationOnce(docId, location) {
    const normalizedId = String(docId || "").trim();
    if (!normalizedId || warnedInvalidLocationCapsuleIds.has(normalizedId)) return;
    warnedInvalidLocationCapsuleIds.add(normalizedId);
    console.warn("Skipping time capsule with invalid location:", {
        capsuleId: normalizedId,
        lat: location?.lat,
        lng: location?.lng,
    });
}


/**
 * Returns all time-capsules within `radius` meters of `center` (lat, lng).
 */
export async function fetchNearbyTimeCapsules({ center, radius = nearByTimeCapulesRadius }) {
    //Fetch radius from public/config file
    let finalRadius = Number(radius);
    if (!Number.isFinite(finalRadius) || finalRadius <= 0) {
        finalRadius = nearByTimeCapulesRadius;
        const config = await loadAppConfig();
        const configRadius = Number(config?.NEAR_BY_TIME_CAPSULES_RADIUS);
        if (Number.isFinite(configRadius) && configRadius > 0) {
            finalRadius = configRadius;
        }
    }
    const auth = getAuth();
    const userId = auth.currentUser?.uid;
    const now = new Date();
    if (!isValidGeoLocation(center?.lat, center?.lng)) {
        console.warn("fetchNearbyTimeCapsules called with invalid center:", center);
        return [];
    }

    // Compute geohash query bounds
    const bounds = geohashQueryBounds([center.lat, center.lng], finalRadius);

    // Fire off both public & (if logged in) private queries
    const snaps = await Promise.all(
        bounds.flatMap(([start, end]) => {
            const pubQ = query(
                collection(db, 'timeCapsules'),
                where('geohash', '>=', start),
                where('geohash', '<=', end),
                where('status', '==', 'published'),
                where('capsuleType', '!=', 'private'),
            );
            const arr = [getDocs(pubQ)];

            if (userId) {
                const privQ = query(
                    collection(db, 'timeCapsules'),
                    where('geohash', '>=', start),
                    where('geohash', '<=', end),
                    where('status', 'in', ['delivered', 'sent']),
                    where('capsuleType', '==', 'private'),
                    where('recipientUserIds', 'array-contains', userId),
                );
                arr.push(getDocs(privQ));
            }

            return arr;
        })
    );

    // Flatten & filter by distance, reveal/end dates
    const allCaps = [];
    snaps.flat().forEach((snap) => {
        snap.docs.forEach((d) => {
            const data = d.data();
            if (isCapsuleEnded(data, now.getTime())) return;
            if (!isValidGeoLocation(data?.location?.lat, data?.location?.lng)) {
                warnInvalidLocationOnce(d.id, data?.location || {});
                return;
            }

            const distMeters = distanceBetween(
                [center.lat, center.lng],
                [data.location.lat, data.location.lng]
            ) * 1000;

            const reveal = data.revealDate?.toDate();
            // allow private always; for others require revealDate <= now or no revealDate
            const isVisible =
                data.capsuleType === 'private' ||
                !reveal ||
                reveal <= now;

            if (distMeters <= finalRadius && isVisible) {
                allCaps.push({ id: d.id, ...data });
            }
        });
    });

    return allCaps;
}

export async function fetchNearbyGems({
    center,
    viewerUid = "",
    radius = nearByTimeCapulesRadius,
    limit = 40,
} = {}) {
    let finalRadius = Number(radius);
    if (!Number.isFinite(finalRadius) || finalRadius <= 0) {
        finalRadius = nearByTimeCapulesRadius;
    }
    if (!isValidGeoLocation(center?.lat, center?.lng)) {
        console.warn("fetchNearbyGems called with invalid center:", center);
        return [];
    }

    const uid = String(viewerUid || "").trim();
    const now = new Date();
    const bounds = geohashQueryBounds([center.lat, center.lng], finalRadius);
    const snaps = await Promise.all(
        bounds.flatMap(([start, end]) => {
            const publicQ = query(
                collection(db, 'timeCapsules'),
                where('geohash', '>=', start),
                where('geohash', '<=', end),
                where('status', '==', 'published'),
                where('capsuleType', '!=', 'private'),
            );
            const queries = [getDocs(publicQ)];

            if (uid) {
                const receivedQ = query(
                    collection(db, 'timeCapsules'),
                    where('geohash', '>=', start),
                    where('geohash', '<=', end),
                    where('status', 'in', ['delivered', 'sent']),
                    where('capsuleType', '==', 'private'),
                    where('recipientUserIds', 'array-contains', uid),
                );
                queries.push(getDocs(receivedQ));
            }

            return queries;
        })
    );

    const gemsById = new Map();
    const senderProfilesById = new Map();
    snaps.flat().forEach((snap) => {
        snap.docs.forEach((docSnap) => {
            const data = docSnap.data() || {};
            if (!isValidGeoLocation(data?.location?.lat, data?.location?.lng)) {
                warnInvalidLocationOnce(docSnap.id, data?.location || {});
                return;
            }

            if (isCapsuleEnded(data, now.getTime())) return;

            const reveal = data.revealDate?.toDate?.();
            const isPrivate = String(data.capsuleType || "").toLowerCase() === "private";
            if (!isPrivate && reveal && reveal > now) return;

            const deletedBy = Array.isArray(data.deletedBy) ? data.deletedBy : [];
            const archivedBy = Array.isArray(data.archivedBy) ? data.archivedBy : [];
            if (uid && (deletedBy.includes(uid) || archivedBy.includes(uid))) return;

            const distMeters = distanceBetween(
                [center.lat, center.lng],
                [data.location.lat, data.location.lng]
            ) * 1000;
            if (distMeters > finalRadius) return;

            const gem = normalizeNearbyGem(
                { id: docSnap.id, ...data },
                { viewerUid: uid, distanceMeters: distMeters }
            );
            if (!gem) return;
            gemsById.set(gem.id, gem);
        });
    });

    const receivedSenderIds = Array.from(
        new Set(
            Array.from(gemsById.values())
                .filter((gem) => gem?.gemKind === "received")
                .map((gem) => String(gem?.raw?.senderId || "").trim())
                .filter(Boolean)
        )
    );
    const senderIdChunks = [];
    for (let index = 0; index < receivedSenderIds.length; index += 10) {
        senderIdChunks.push(receivedSenderIds.slice(index, index + 10));
    }
    const senderSnaps = await Promise.all(
        senderIdChunks.map((chunk) =>
            getDocs(query(collection(db, 'users'), where(documentId(), 'in', chunk))).catch((err) => {
                console.warn("Failed to load nearby gem sender profiles:", chunk, err);
                return null;
            })
        )
    );
    senderSnaps.forEach((snap) => {
        if (!snap) return;
        snap.docs.forEach((docSnap) => {
            const data = docSnap.data() || {};
            senderProfilesById.set(docSnap.id, {
                displayName: String(data.displayName || "").trim(),
                publicHandle: String(data.publicHandle || "").trim(),
                userName: String(data.userName || "").trim(),
                avatarUrl: String(data.profileImage || data.avatarUrl || data.photoURL || "").trim(),
            });
        });
    });

    senderProfilesById.forEach((profile, senderId) => {
        gemsById.forEach((gem, gemId) => {
            if (gem?.gemKind !== "received") return;
            if (String(gem?.raw?.senderId || "").trim() !== senderId) return;
            const senderLabel =
                profile.displayName ||
                (profile.publicHandle ? `@${profile.publicHandle.replace(/^@+/, "")}` : "") ||
                profile.userName;
            if (!senderLabel) return;
            gemsById.set(gemId, {
                ...gem,
                senderLabel,
                senderAvatarUrl: gem.senderAvatarUrl || profile.avatarUrl || "",
            });
        });
    });

    // Member exclusive gems are included: their icon never changes, but their
    // labels come from the same business document.
    const businessIds = Array.from(
        new Set(
            Array.from(gemsById.values())
                .filter((gem) => gem?.gemKind === "sponsored" || gem?.gemKind === "memberExclusive")
                .map((gem) => String(gem?.raw?.businessId || gem?.raw?.senderId || "").trim())
                .filter(Boolean)
        )
    );
    const businessMarkerMap = await getSponsoredBusinessMarkerMap(businessIds);
    applyBusinessGemLabels({
        gems: Array.from(gemsById.values()),
        businessMarkerMap,
    }).forEach((gem) => {
        gemsById.set(gem.id, gem);
    });

    return sortNearbyGems(Array.from(gemsById.values())).slice(0, Math.max(1, Number(limit) || 40));
}

export async function fetchUnCoverTimeCapsules({ center,isUnCover, radius = unCoverTimeCapulesRadius }) {
    //Fetch radius from public/config file
    let finalRadius = Number(radius);
    if (!Number.isFinite(finalRadius) || finalRadius <= 0) {
        finalRadius = unCoverTimeCapulesRadius;
        const config = await loadAppConfig();
        const configRadius = Number(config?.UNCOVER_TIME_CAPSULES_RADIUS);
        if (Number.isFinite(configRadius) && configRadius > 0) {
            finalRadius = configRadius;
        }
    }

    const auth = getAuth();
    const userId = auth.currentUser?.uid;
    const now = new Date();
    if (!isValidGeoLocation(center?.lat, center?.lng)) {
        console.warn("fetchUnCoverTimeCapsules called with invalid center:", center);
        return [];
    }

    // Compute geohash query bounds
    const bounds = geohashQueryBounds([center.lat, center.lng], finalRadius);

    // Fire off both public & (if logged in) private queries
    const snaps = await Promise.all(
        bounds.flatMap(([start, end]) => {
            let arr = [];
            if(isUnCover){
                const pubQ = query(
                    collection(db, 'timeCapsules'),
                    where('geohash', '>=', start),
                    where('geohash', '<=', end),
                    where('status', '==', 'published'),
                    where('capsuleType', '!=', 'private'),
                );
                arr = [getDocs(pubQ)];
            }

            if (userId) {
                const privQ = query(
                    collection(db, 'timeCapsules'),
                    where('geohash', '>=', start),
                    where('geohash', '<=', end),
                    where('status', 'in', ['delivered', 'sent']),
                    where('capsuleType', '==', 'private'),
                    where('recipientUserIds', 'array-contains', userId),
                );
                arr.push(getDocs(privQ));

                if (isUnCover) {
                    const sentPrivQ = query(
                        collection(db, 'timeCapsules'),
                        where('geohash', '>=', start),
                        where('geohash', '<=', end),
                        where('status', 'in', ['delivered', 'sent']),
                        where('capsuleType', '==', 'private'),
                        where('senderId', '==', userId),
                    );
                    arr.push(getDocs(sentPrivQ));
                }
            }

            return arr;
        })
    );

    // Flatten & filter by distance, reveal/end dates
    const allCapsById = new Map();
    snaps.flat().forEach((snap) => {
        snap.docs.forEach((d) => {
            const data = d.data();
            if (isCapsuleEnded(data, now.getTime())) return;
            if (!isValidGeoLocation(data?.location?.lat, data?.location?.lng)) {
                warnInvalidLocationOnce(d.id, data?.location || {});
                return;
            }

            const distMeters = distanceBetween(
                [center.lat, center.lng],
                [data.location.lat, data.location.lng]
            ) * 1000;

            const reveal = data.revealDate?.toDate();
            // allow private always; for others require revealDate <= now or no revealDate
            const isVisible = isUnCover ? true :
                data.capsuleType === 'private' ||
                !reveal ||
                reveal <= now;

            const archivedBy = data.archivedBy || [];
            const isNotArchived = !archivedBy.includes(userId)

            const readBy = data.readBy || [];
            const isUnread = isUnCover ? true : !readBy.includes(userId);

            const deletedBy = data.deletedBy || [];
            const isNotDeleted = !deletedBy.includes(userId);

            if (distMeters <= finalRadius && isVisible && isUnread && isNotArchived && isNotDeleted) {
                allCapsById.set(d.id, { id: d.id, ...data });
            }
        });
    });

    return Array.from(allCapsById.values());
}

export async function fetchUserDraftTimeCapsules(userId) {
    if (!userId) return [];

    const snaps = await getDocs(
        query(collection(db, 'timeCapsules'), where('senderId', '==', userId))
    );

    const drafts = [];
    snaps.forEach((d) => {
        const data = d.data() || {};
        const isDraft =
            data.lifecycleStatus === 'draft' ||
            data.status === 'draft' ||
            data.isDraft === true;
        if (!isDraft) return;

        drafts.push({ id: d.id, ...data });
    });

    drafts.sort((a, b) => {
        const aDate =
            a.lastEditedAt?.toDate?.() ||
            a.createdAt?.toDate?.() ||
            (a.lastEditedAt ? new Date(a.lastEditedAt) : (a.createdAt ? new Date(a.createdAt) : new Date(0)));
        const bDate =
            b.lastEditedAt?.toDate?.() ||
            b.createdAt?.toDate?.() ||
            (b.lastEditedAt ? new Date(b.lastEditedAt) : (b.createdAt ? new Date(b.createdAt) : new Date(0)));
        return bDate - aDate;
    });

    return drafts;
}

export async function fetchUserSentTimeCapsules(userId) {
    if (!userId) return [];

    const snaps = await getDocs(
        query(collection(db, 'timeCapsules'), where('senderId', '==', userId))
    );

    const sentCapsules = [];
    snaps.forEach((d) => {
        const data = d.data() || {};
        const isPrivateCapsule = String(data.capsuleType || "").toLowerCase() === "private";
        if (!isPrivateCapsule) return;

        const isDraft =
            data.lifecycleStatus === 'draft' ||
            data.status === 'draft' ||
            data.isDraft === true;
        if (isDraft) return;

        const status = String(data.status || "").toLowerCase();
        const lifecycleStatus = String(data.lifecycleStatus || "").toLowerCase();
        const isSentLike =
            ["sent", "delivered", "published"].includes(status) ||
            ["sent", "delivered", "published", "completed"].includes(lifecycleStatus);

        if (!isSentLike) return;

        sentCapsules.push({ id: d.id, ...data });
    });

    sentCapsules.sort((a, b) => {
        const aDate =
            a.sentAt?.toDate?.() ||
            a.createdAt?.toDate?.() ||
            (a.sentAt ? new Date(a.sentAt) : (a.createdAt ? new Date(a.createdAt) : new Date(0)));
        const bDate =
            b.sentAt?.toDate?.() ||
            b.createdAt?.toDate?.() ||
            (b.sentAt ? new Date(b.sentAt) : (b.createdAt ? new Date(b.createdAt) : new Date(0)));
        return bDate - aDate;
    });

    return sentCapsules;
}

export async function fetchProfileTimeCapsules(userId) {
    if (!userId) return [];

    const snap = await getDocs(
        query(collection(db, 'timeCapsules'), where('senderId', '==', userId))
    );

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    const capsules = snap.docs
        .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
        .filter((capsule) => {
            if (capsule.capsuleType === 'private') return false;
            if (capsule.lifecycleStatus === 'draft' || capsule.status === 'draft' || capsule.isDraft) {
                return false;
            }

            const revealDate = capsule.revealDate?.toDate?.() || null;
            if (revealDate && revealDate > now) return false;

            const endDate = capsule.endDate?.toDate?.() || null;
            if (endDate) {
                const end = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
                if (end < today) return false;
            }

            return ['public', 'memberExclusive', 'sponsored'].includes(capsule.capsuleType);
        });

    capsules.sort((a, b) => {
        const aDate =
            a.sentAt?.toDate?.() ||
            a.createdAt?.toDate?.() ||
            (a.sentAt ? new Date(a.sentAt) : (a.createdAt ? new Date(a.createdAt) : new Date(0)));
        const bDate =
            b.sentAt?.toDate?.() ||
            b.createdAt?.toDate?.() ||
            (b.sentAt ? new Date(b.sentAt) : (b.createdAt ? new Date(b.createdAt) : new Date(0)));

        return bDate - aDate;
    });

    return capsules;
}

export async function fetchTimeCapsulesByIds(capsuleIds = [], viewerUserId = "") {
    const ids = Array.isArray(capsuleIds)
        ? capsuleIds.map((id) => String(id || "").trim()).filter(Boolean)
        : [];
    const viewerUid = String(viewerUserId || "").trim();

    if (!ids.length) return [];

    const chunks = [];
    for (let i = 0; i < ids.length; i += 10) {
        chunks.push(ids.slice(i, i + 10));
    }

    const snaps = await Promise.all(
        chunks.map((chunk) =>
            getDocs(query(collection(db, 'timeCapsules'), where(documentId(), 'in', chunk)))
        )
    );

    const byId = new Map();
    snaps.forEach((snap) => {
        snap.docs.forEach((docSnap) => {
            const data = docSnap.data() || {};
            const isPrivate = data.capsuleType === 'private';
            const canSeePrivate =
                isPrivate &&
                viewerUid &&
                (
                    data.senderId === viewerUid ||
                    (Array.isArray(data.recipientUserIds) && data.recipientUserIds.includes(viewerUid)) ||
                    (Array.isArray(data.claimedBy) && data.claimedBy.includes(viewerUid))
                );

            if (isPrivate && !canSeePrivate) return;
            if (!isPrivate && data.status !== 'published') return;
            // Loved and found-Ask capsules are looked up by id, so they skip
            // the geo queries' end-date filter. Without this they keep
            // surfacing in the nearby sheet after their run has ended.
            if (isCapsuleEnded(data)) return;
            byId.set(docSnap.id, { id: docSnap.id, ...data });
        });
    });

    return ids
        .map((id) => byId.get(id))
        .filter(Boolean);
}

// Short-lived cache for a viewer's loved capsules, keyed by the loved id-set.
// Both the home map and the history route load the same loved capsules from the
// profile store; this dedupes the batched read across routes/remounts. The key
// changes whenever the loved set changes, so loves/unloves are reflected.
const LOVED_CAPSULES_CACHE_TTL_MS = 60 * 1000;
const lovedCapsulesCache = new Map();

export function fetchLovedTimeCapsules(lovedCapsuleIds = [], viewerUserId = "") {
    const ids = Array.from(
        new Set(
            (Array.isArray(lovedCapsuleIds) ? lovedCapsuleIds : [])
                .map((id) => String(id || "").trim())
                .filter(Boolean)
        )
    );
    if (!ids.length) return Promise.resolve([]);

    const viewerUid = String(viewerUserId || "").trim();
    const key = `${viewerUid}::${[...ids].sort().join(",")}`;
    const now = Date.now();
    const cached = lovedCapsulesCache.get(key);
    if (cached && now - cached.at <= LOVED_CAPSULES_CACHE_TTL_MS) {
        return cached.promise;
    }

    const promise = fetchTimeCapsulesByIds(ids, viewerUid).catch((err) => {
        // Don't cache failures so the next call retries.
        if (lovedCapsulesCache.get(key)?.promise === promise) {
            lovedCapsulesCache.delete(key);
        }
        throw err;
    });
    lovedCapsulesCache.set(key, { at: now, promise });
    return promise;
}

export async function fetchUserFoundAskTimeCapsules(userId = "") {
    const uid = String(userId || "").trim();
    if (!uid) return [];

    const snap = await getDocs(
        query(collection(db, 'askDiscoveryGrants'), where('uid', '==', uid))
    );
    const capsuleIds = Array.from(
        new Set(
            snap.docs
                .map((docSnap) => String(docSnap.data()?.capsuleId || "").trim())
                .filter(Boolean)
        )
    );

    return fetchTimeCapsulesByIds(capsuleIds, uid);
}
/**
 * Fetch a single time-capsule by ID.
 */
export async function fetchTimeCapsuleById(id) {
    const snap = await getDoc(doc(db, 'timeCapsules', id));
    return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}
